import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PromoBanner from "../components/PromoBanner";
import {
  Play, Save, Plus, Trash2, ArrowUpRight, ArrowDownRight, Award,
  TrendingUp, TrendingDown, Sparkles, Lock, Download,
} from "lucide-react";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";

const INDICATORS = ["rsi", "macd", "macd_hist", "vwap", "ema9", "ema20", "ema50",
                    "sma20", "sma50", "sma200", "bb_upper", "bb_lower", "atr", "rvol", "close", "high", "low"];
const OPS = [">", "<", ">=", "<=", "==", "!="];
const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];
const PERIODS = ["1mo", "3mo", "6mo", "1y", "2y", "5y", "max"];

const DEFAULT_STRATEGY = {
  name: "RSI oversold + VWAP",
  symbol: "AAPL",
  timeframe: "15m",
  period: "3mo",
  entry: {
    operator: "AND",
    rules: [
      { indicator: "rsi", op: "<", value: 30 },
      { indicator: "close", op: ">", value_indicator: "vwap" },
    ],
  },
  exit: { stop_loss_pct: 2, take_profit_pct: 5, time_exit_bars: 20 },
};

function Metric({ label, value, color, prefix, suffix }) {
  return (
    <div className="card-surface p-3">
      <div className="label-mono">{label}</div>
      <div className={`font-mono text-lg tabular-nums font-semibold mt-1 ${color || "text-white"}`}>
        {prefix || ""}{value}{suffix || ""}
      </div>
    </div>
  );
}

function RuleRow({ rule, onChange, onRemove }) {
  return (
    <div className="flex items-center gap-1.5">
      <select value={rule.indicator} onChange={(e) => onChange({ ...rule, indicator: e.target.value })}
              className="input-glass py-1 text-[12px] flex-1">
        {INDICATORS.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
      <select value={rule.op} onChange={(e) => onChange({ ...rule, op: e.target.value })}
              className="input-glass py-1 text-[12px] w-16">
        {OPS.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {rule.value_indicator ? (
        <select value={rule.value_indicator}
                onChange={(e) => onChange({ ...rule, value_indicator: e.target.value, value: undefined })}
                className="input-glass py-1 text-[12px] flex-1">
          {INDICATORS.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      ) : (
        <input type="number" step="any" value={rule.value ?? 0}
               onChange={(e) => onChange({ ...rule, value: parseFloat(e.target.value), value_indicator: undefined })}
               className="input-glass py-1 text-[12px] flex-1"/>
      )}
      <button onClick={() => onChange({ ...rule,
                                       value_indicator: rule.value_indicator ? undefined : "vwap",
                                       value: rule.value_indicator ? 0 : undefined })}
              className="text-[10px] label-mono px-2 py-1 border border-white/10 rounded hover:border-white/30">
        {rule.value_indicator ? "→ num" : "→ ind"}
      </button>
      <button onClick={onRemove} className="text-white/40 hover:text-[var(--bear)] p-1">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function Backtester() {
  const { plan, showUpgrade } = usePlan();
  const [strat, setStrat] = useState(DEFAULT_STRATEGY);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);

  const gated = plan !== "executive";

  useEffect(() => {
    if (gated) return;
    api.get("/api/backtester/strategies").then(({ data }) => setSaved(data || [])).catch(() => {});
  }, [gated]);

  const run = async () => {
    if (gated) { showUpgrade("backtester_gated"); return; }
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post("/api/backtester/run", { strategy: strat });
      setResult(data);
      toast.success(`Backtest complete: ${data.metrics.trades} trades`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backtest failed");
    } finally { setRunning(false); }
  };

  const save = async () => {
    if (gated) { showUpgrade("backtester_gated"); return; }
    try {
      await api.post("/api/backtester/strategies", strat);
      toast.success("Strategy saved");
      const { data } = await api.get("/api/backtester/strategies");
      setSaved(data || []);
    } catch (err) { toast.error("Save failed"); }
  };

  const loadSaved = (s) => setStrat(s);
  const deleteSaved = async (s) => {
    if (!confirm(`Delete strategy "${s.name}"?`)) return;
    try {
      await api.delete(`/api/backtester/strategies/${s.id}`);
      const { data } = await api.get("/api/backtester/strategies");
      setSaved(data || []);
    } catch (err) { toast.error("Delete failed"); }
  };

  const setRule = (i, r) => {
    const rules = [...strat.entry.rules];
    rules[i] = r;
    setStrat({ ...strat, entry: { ...strat.entry, rules } });
  };
  const removeRule = (i) => {
    const rules = strat.entry.rules.filter((_, idx) => idx !== i);
    setStrat({ ...strat, entry: { ...strat.entry, rules } });
  };
  const addRule = () => setStrat({
    ...strat,
    entry: { ...strat.entry, rules: [...strat.entry.rules, { indicator: "rsi", op: "<", value: 30 }] },
  });

  const m = result?.metrics;

  return (
    <div className="min-h-screen flex flex-col" data-testid="backtester-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[1600px] w-full mx-auto px-4 sm:px-6 py-8 relative">
        {/* Blurred lock overlay for non-executives */}
        {gated && (
          <div className="absolute inset-0 z-30 bg-black/70 backdrop-blur-lg flex items-center justify-center p-6" data-testid="backtester-gated">
            <div className="card-surface p-6 max-w-md text-center">
              <Lock className="w-6 h-6 text-[#ffd166] mx-auto" />
              <h2 className="text-white text-xl font-semibold mt-3">Backtesting is an Executive Feature.</h2>
              <p className="text-white/60 text-sm mt-2">
                Design, backtest and compare strategies with unlimited historical data — Executive only.
              </p>
              <button onClick={() => showUpgrade("backtester_gated")}
                      className="btn-accent mt-4" data-testid="backtester-upgrade-cta">
                <Sparkles className="w-3.5 h-3.5 mr-1.5 inline" /> Upgrade to Executive
              </button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="label-mono">Backtester</div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold tracking-tight mt-1">Professional Backtester</h1>
          <div className="text-white/50 text-[12px] mt-1">Executive-only. Powered by yfinance historical data.</div>
        </div>

        <div className={`grid grid-cols-1 xl:grid-cols-[380px_1fr] gap-4 ${gated ? "pointer-events-none opacity-50 select-none" : ""}`}>
          {/* Left: strategy builder */}
          <aside className="space-y-4">
            <div className="card-surface p-4" data-testid="strategy-builder">
              <div className="text-white text-[14px] font-semibold mb-3">Strategy</div>
              <div className="space-y-2.5">
                <div>
                  <label className="label-mono">Name</label>
                  <input value={strat.name} onChange={(e) => setStrat({ ...strat, name: e.target.value })}
                         className="input-glass w-full mt-1" data-testid="strategy-name"/>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="label-mono">Symbol</label>
                    <input value={strat.symbol}
                           onChange={(e) => setStrat({ ...strat, symbol: e.target.value.toUpperCase() })}
                           className="input-glass w-full mt-1" data-testid="strategy-symbol"/>
                  </div>
                  <div>
                    <label className="label-mono">TF</label>
                    <select value={strat.timeframe} onChange={(e) => setStrat({ ...strat, timeframe: e.target.value })}
                            className="input-glass w-full mt-1" data-testid="strategy-timeframe">
                      {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono">Period</label>
                    <select value={strat.period} onChange={(e) => setStrat({ ...strat, period: e.target.value })}
                            className="input-glass w-full mt-1" data-testid="strategy-period">
                      {PERIODS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Entry rules */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="label-mono">Entry rules</div>
                  <select value={strat.entry.operator}
                          onChange={(e) => setStrat({ ...strat, entry: { ...strat.entry, operator: e.target.value } })}
                          className="input-glass py-1 text-[11px] w-20">
                    <option value="AND">AND</option>
                    <option value="OR">OR</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  {strat.entry.rules.map((r, i) => (
                    <RuleRow key={i} rule={r} onChange={(nr) => setRule(i, nr)} onRemove={() => removeRule(i)} />
                  ))}
                </div>
                <button onClick={addRule} className="btn-ghost text-[12px] mt-2 inline-flex items-center gap-1.5"
                        data-testid="add-rule-btn">
                  <Plus className="w-3 h-3" /> Add rule
                </button>
              </div>

              {/* Exit rules */}
              <div className="mt-4">
                <div className="label-mono mb-2">Exit rules</div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <div className="text-[10px] text-white/40">Stop %</div>
                    <input type="number" step="any" value={strat.exit.stop_loss_pct || ""}
                           onChange={(e) => setStrat({ ...strat, exit: { ...strat.exit, stop_loss_pct: parseFloat(e.target.value) || 0 } })}
                           className="input-glass w-full py-1 text-[12px] mt-1"/>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/40">Take %</div>
                    <input type="number" step="any" value={strat.exit.take_profit_pct || ""}
                           onChange={(e) => setStrat({ ...strat, exit: { ...strat.exit, take_profit_pct: parseFloat(e.target.value) || 0 } })}
                           className="input-glass w-full py-1 text-[12px] mt-1"/>
                  </div>
                  <div>
                    <div className="text-[10px] text-white/40">Time bars</div>
                    <input type="number" step="1" value={strat.exit.time_exit_bars || ""}
                           onChange={(e) => setStrat({ ...strat, exit: { ...strat.exit, time_exit_bars: parseInt(e.target.value) || 0 } })}
                           className="input-glass w-full py-1 text-[12px] mt-1"/>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <button onClick={run} disabled={running} className="btn-accent flex-1 inline-flex items-center justify-center gap-2"
                        data-testid="run-backtest-btn">
                  <Play className="w-3.5 h-3.5" /> {running ? "Running…" : "Run"}
                </button>
                <button onClick={save} className="btn-ghost inline-flex items-center gap-1.5"
                        data-testid="save-strategy-btn">
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
              </div>
            </div>

            {/* Saved strategies */}
            <div className="card-surface p-3">
              <div className="text-white text-[14px] font-semibold px-1 mb-2">Saved strategies</div>
              {saved.length === 0 && <div className="text-white/40 text-xs px-1 py-4">None saved yet</div>}
              <div className="space-y-1">
                {saved.map((s) => (
                  <div key={s.id} className="group flex items-center justify-between px-2 py-2 rounded-lg hover:bg-white/[0.03]"
                       data-testid={`saved-strategy-${s.id}`}>
                    <button onClick={() => loadSaved(s)} className="text-left flex-1 min-w-0">
                      <div className="text-white text-[13px] font-semibold tracking-tight truncate">{s.name}</div>
                      <div className="text-[10px] label-mono mt-0.5">
                        {s.symbol} · {s.timeframe} · {s.period}
                        {s.last_metrics && (
                          <span className="ml-2 text-[var(--bull)]">
                            {s.last_metrics.win_rate}% WR
                          </span>
                        )}
                      </div>
                    </button>
                    <button onClick={() => deleteSaved(s)} className="text-white/40 hover:text-[var(--bear)] p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          {/* Right: results */}
          <section className="space-y-4">
            {!result && (
              <div className="card-surface p-8 text-center text-white/50">
                Run the strategy to see performance metrics, an equity curve and the full trade log.
              </div>
            )}
            {result && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Metric label="Trades" value={m.trades} />
                  <Metric label="Win Rate" value={m.win_rate} suffix="%" color={m.win_rate >= 50 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
                  <Metric label="Profit Factor" value={m.profit_factor} />
                  <Metric label="Total Return" value={m.total_return_pct} suffix="%"
                          color={m.total_return_pct >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"} />
                  <Metric label="Max Drawdown" value={m.max_drawdown_pct} suffix="%" color="text-[var(--bear)]" />
                  <Metric label="Sharpe" value={m.sharpe} />
                  <Metric label="Sortino" value={m.sortino} />
                  <Metric label="Expectancy" value={m.expectancy} prefix="$" />
                  <Metric label="Largest Win" value={m.largest_win} prefix="$" color="text-[var(--bull)]" />
                  <Metric label="Largest Loss" value={m.largest_loss} prefix="$" color="text-[var(--bear)]" />
                  <Metric label="Avg Winner" value={m.avg_winner} prefix="$" color="text-[var(--bull)]" />
                  <Metric label="Avg Loser" value={m.avg_loser} prefix="$" color="text-[var(--bear)]" />
                </div>

                {/* Equity curve */}
                <div className="card-surface p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-[var(--bull)]" />
                      <div className="text-white text-[14px] font-semibold">Equity curve</div>
                    </div>
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/backtester/results/${result.id}/export.csv`}
                      target="_blank" rel="noreferrer"
                      className="btn-ghost text-[11px] inline-flex items-center gap-1.5"
                      data-testid="export-csv-btn"
                    >
                      <Download className="w-3 h-3" /> Export CSV
                    </a>
                  </div>
                  <div style={{ width: "100%", height: 220 }}>
                    <ResponsiveContainer>
                      <LineChart data={result.equity_curve}>
                        <XAxis dataKey="ts" hide />
                        <YAxis domain={["auto", "auto"]} stroke="#666" tick={{ fontSize: 10 }}/>
                        <Tooltip contentStyle={{ background: "#0e0e14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}/>
                        <Line dataKey="equity" stroke="#00ff9c" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Trade log */}
                <div className="card-surface p-0 overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                    <Award className="w-3.5 h-3.5 text-white/60" />
                    <div className="text-white text-[14px] font-semibold">Trade log ({result.trades.length})</div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-white/[0.02] text-[10px] font-mono uppercase tracking-wider text-white/55">
                          <th className="text-left px-3 py-2.5">Entry</th>
                          <th className="text-left px-3 py-2.5">Exit</th>
                          <th className="text-right px-3 py-2.5">Entry $</th>
                          <th className="text-right px-3 py-2.5">Exit $</th>
                          <th className="text-right px-3 py-2.5">P/L $</th>
                          <th className="text-right px-3 py-2.5">P/L %</th>
                          <th className="text-right px-3 py-2.5">Bars</th>
                          <th className="text-left px-3 py-2.5">Exit reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.trades.slice(0, 100).map((t, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="px-3 py-2 text-white/70 text-[11px]">{String(t.entry_ts).slice(0, 16)}</td>
                            <td className="px-3 py-2 text-white/70 text-[11px]">{String(t.exit_ts).slice(0, 16)}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">{t.entry_price}</td>
                            <td className="px-3 py-2 text-right font-mono tabular-nums">{t.exit_price}</td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${t.pl_usd >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"}`}>
                              {t.pl_usd >= 0 ? <ArrowUpRight className="inline w-3 h-3 mr-0.5" /> : <ArrowDownRight className="inline w-3 h-3 mr-0.5" />}
                              {t.pl_usd}
                            </td>
                            <td className={`px-3 py-2 text-right font-mono tabular-nums ${t.pl_pct >= 0 ? "text-[var(--bull)]" : "text-[var(--bear)]"}`}>{t.pl_pct}%</td>
                            <td className="px-3 py-2 text-right text-white/60">{t.hold_bars}</td>
                            <td className="px-3 py-2 text-white/70 text-[11px]">{t.exit_reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
